import { Component, Input, OnInit, SimpleChanges } from "@angular/core";
import { FilterableFieldStaticValue, FilterableFieldDynamicValue, FilterableField, FilterableFieldSource,} from "src/shared/models/pipeline-config/reporting-sets.models";
import { ReportingSetDynamicFiltersService } from "./reportingset-dynamic-filters.service";
import { CVLocalizationService } from "../services/cv-localization.service";
import { ModuleManager } from "src/shared/models/module-manager";
import { cloneDeep } from 'lodash';
import { ModuleManagerSetting } from "src/shared/models/module-manager/module-manager-setting.model";

interface StateValues {
    Values: { [fieldName: string]: any[] };
    ReportID: string;
}

@Component({
    selector: "cv-reportingset-dynamic-filters",
    templateUrl: "./reportingset-dynamic-filters.component.html",
    styleUrl: "./reportingset-dynamic-filters.component.scss",
})
export class ReportingSetDynamicFiltersComponent implements OnInit {
    @Input() externalReportFilterableFields: { [FieldName: string]: FilterableField };
    @Input() moduleManager: ModuleManager;
    @Input() selectedReportID: string = null;

    public selectedStaticFilterableFields: { [key: string]: FilterableFieldStaticValue[]; } = {};
    public selectedDynamicFilterableFields: { [key: string]: FilterableFieldDynamicValue[]; } = {};
    public dataLoaded = false;
    public selectedReportFilterableFields: { [FieldName: string]: FilterableField };
    public loadfromModuleManager: boolean = false;
    public moduleManagerCopy: { [FieldName: string]: FilterableField };
    public dynamicState: any[];
    public stateValues: StateValues = {ReportID: "", Values: {}};
    public isInitializing = false; // flag to indivate the models are being initialized or reinitialized
    public isFirstLoad = true;

    constructor(
        private service: ReportingSetDynamicFiltersService,
        private cvLocalizationFactory: CVLocalizationService, 
    ) {}

    async ngOnInit() {
        await this.cvLocalizationFactory.GetSection("Report_Widget");
        this.stateValues = {ReportID: "", Values: {}};

        await this.moduleManager.addSetting({
            name: 'reportFilterableFields',
            required: true,
            onMissingValue: () => {
                this.stateValues = {ReportID: "", Values: {}}
            },
            getValue: () => {
                const filteredValues = this.preparesFilteredValuesForState(this.stateValues);

                const stateValues = cloneDeep(this.stateValues);
                stateValues.Values = filteredValues;

                return stateValues;
            },
            onSettingLoaded: async (value) => {
                if (this.isFirstLoad) {
                    this.selectedReportFilterableFields = cloneDeep(this.externalReportFilterableFields);
                    this.moduleManager.filterablesComponent = this;
                    this.isFirstLoad = false;
                }

                if (value && value.ReportID === this.selectedReportID) {
                    this.loadfromModuleManager = true;
                    this.moduleManagerCopy = cloneDeep(value);
                    await this.setModels();
                } else {
                    await this.InitializeFilters();
                    this.stateValues.ReportID = this.selectedReportID;
                }

                this.dataLoaded = true;
            },
            defaultValue: undefined,
        } as ModuleManagerSetting<StateValues>);
    }

    async ngOnChanges(changes: SimpleChanges) {
        //When the component is loaded the first time, ngOnChanges is called, therefore, this.isInitializing === true is applied and setModels() cannot not complete the process in the onSettingLoaded() function.
        if (this.isFirstLoad) {
            return;
        }

        if (changes.externalReportFilterableFields) {
            this.selectedReportFilterableFields = cloneDeep(this.externalReportFilterableFields);
            if (!this.loadfromModuleManager) {
                await this.InitializeFilters();
                this.stateValues.ReportID = this.selectedReportID;
            }
            else {
                await this.setModels();
            }
        }
    }

    // Cache of overridden max selection values to restore from when switching the view by
    private overridenMaxSelectionValues = {};

    /**
     * This function is used to override the max selection value for a given filterable field
     * @param filterableKey The key identifying the filter field
     * @param newMaxSelection The new max selection
     */
    public overrideMaxSelection(filterableKey: string, newMaxSelection: number) {
        const currentMax = this.selectedReportFilterableFields[filterableKey].MaxAllowedSelections;
        if (this.overridenMaxSelectionValues[filterableKey] === null) {
            this.overridenMaxSelectionValues[filterableKey] = currentMax;
        }
        this.selectedReportFilterableFields[filterableKey].MaxAllowedSelections = newMaxSelection;
    }

    /**
     * This function is used to restore the max selection values for all filterable fields
     */
    public restoreAllMaxSelectionOverrides() {
        if (this.overridenMaxSelectionValues !== null) {
            for (const key in this.overridenMaxSelectionValues) {
                this.selectedReportFilterableFields[key].MaxAllowedSelections = this.overridenMaxSelectionValues[key];
            }
        }
    }

    /**
     * This functions transforms and prepares the reportFilterableFields values that will be saved in the module state
     * @param stateValues The StateValue that contains all the filterable fields
     * @returns and object that contains the value that will be assigned to the state value.
     */
    public preparesFilteredValuesForState(stateValues: StateValues) {
        let filteredValues = Object.entries(stateValues.Values)
            .filter(([key, value]) => value.length > 0)
            .reduce((obj, [key, value]) => {
                obj[key] = value;
                return obj;
            }, {});

        // iterate through all state values
        filteredValues = Object.entries(stateValues.Values).reduce((result, [key, value]) => {
            const filterField = this.selectedReportFilterableFields[key];

            if (!filterField) {
                return result;
            }

            // check its source
            const maxSelectionLength = filterField.ValueSource === FilterableFieldSource.Static ? filterField.StaticValues?.length : filterField.DynamicValues?.length;

            if (value.length === maxSelectionLength) {
                result[key] = ["# ALL #"];
            } else {
                result[key] = value;
            }

            return result;
        }, {})

        return filteredValues;
    }

    /**
     * Initialize the filters by setting up default values and configurations.
     * This function prepares the filter settings to be used in the component on first initilization.
     * does not pull in the state
     * */
    public async InitializeFilters() {
        if (this.isInitializing === true)
            return;
        this.isInitializing = true;

        for (const key in this.selectedReportFilterableFields) {
            await this.loadInitialFilterSelection(key);
        }
        this.isInitializing = false;
    }

    /**
     * This function populates the selected filters for a given key based on default settings
     * should be used on initialization
     * @param key The key identifying the filter field
     */
    private async loadInitialFilterSelection(key: string) {
        let filterField = this.selectedReportFilterableFields[key];

        if (filterField.ValueSource !== 1) {
            if (!this.selectedStaticFilterableFields[key]) {
                this.selectedStaticFilterableFields[key] = [];
            }

            if (filterField.MinRequiredSelections > 0) {
                this.stateValues.Values[key] = [];
                if (filterField.MaxAllowedSelections === 1) {
                    //this is a single select filter
                    filterField.StaticValues[0].selected = true;
                    this.selectedStaticFilterableFields[key].push(filterField.StaticValues[0]);
                    this.stateValues.Values[key].push(filterField.StaticValues[0].Value);
                } else {
                    //this is a multi select filter
                    for (let i = 0; i < filterField.MinRequiredSelections; i++) {
                        filterField.StaticValues[i].selected = true;
                        this.selectedStaticFilterableFields[key].push(filterField.StaticValues[i]);
                        this.stateValues.Values[key].push(filterField.StaticValues[i].Value);
                    }
                }
            }
            else if (filterField.MinRequiredSelections === -1) {
                this.selectMaxFilterOptions(key);
            }
        } else {
            //this handles the dynamic filters. will have to make an api call here
            this.selectedReportFilterableFields[key].DynamicValues = [];
            await this.populateDynamicValues(filterField.ValueSourceReportingSet, filterField.ValueDisplayField, key);

            if (this.selectedReportFilterableFields[key].DynamicValues?.length === 0 || this.selectedReportFilterableFields[key].DynamicValues === undefined) {
                delete this.selectedReportFilterableFields[key];
                return;
            }

            if (!this.selectedDynamicFilterableFields[key]) {
                this.selectedDynamicFilterableFields[key] = [];
            }

            if (filterField.MinRequiredSelections > 0) {
                this.stateValues.Values[key] = [];
                if (filterField.MaxAllowedSelections === 1) {
                    filterField.DynamicValues[0].selected = true;
                    this.selectedDynamicFilterableFields[key].push(filterField.DynamicValues[0]);
                    this.stateValues.Values[key].push(filterField.DynamicValues[0].Value);
                } else {
                    //this is a multi select filter
                    for (let i = 0; i < filterField.MinRequiredSelections; i++) {
                        filterField.DynamicValues[i].selected = true;
                        this.selectedDynamicFilterableFields[key].push(filterField.DynamicValues[i]);
                        this.stateValues.Values[key].push(filterField.DynamicValues[i].Value);
                    }
                }
            }
            else if (filterField.MinRequiredSelections === -1) {
                this.selectMaxFilterOptions(key);
            }
        }
    }

    /**
     * Populate the dynamic values for the filters by fetching data from the backend.
     * This function loads the dynamic values into their respective variables for use in the component.
     * */
    public async populateDynamicValues(reportingSet: string, displayField: string, key: string ) {
        const response = await this.service.loadReportFilters(reportingSet, displayField, this.selectedReportFilterableFields[key].ValueField);
        if (response.length > 0) {
            response.forEach((row) => {
                const dynamicValue: FilterableFieldDynamicValue = {
                    DisplayValue: row.DisplayValue, // Assuming row is the DisplayValue
                    selected: false,
                    Value: row.Value,
                };

                if (this.selectedReportFilterableFields[key].DynamicValues === undefined) {
                    this.selectedReportFilterableFields[key].DynamicValues = [];
                }
        
                this.selectedReportFilterableFields[key].DynamicValues.push(dynamicValue);
            });
        }
    }

    /**
    * Compare the current state values with new values and populate the similarities.
    * This function updates the dynamic values by fetching new data and comparing it with the existing data.
    * */
    public async compareAndPopulateDynamicValues(reportingSet: string, displayField: string, key: string) {
        const response = await this.service.loadReportFilters(reportingSet, displayField, this.selectedReportFilterableFields[key].ValueField);
        if (response.length > 0) {
            //Double check if DynamicValues property of  this.selectedReportFilterableFields[key] is undefined, if true initialize DynamicValues = []
            if (this.selectedReportFilterableFields[key]?.DynamicValues === undefined) {
                this.selectedReportFilterableFields[key].DynamicValues = [];
            }

            for (let i = 0; i < response.length; i++) {
                const row = response[i];
                const dynamicValue: FilterableFieldDynamicValue = {
                    DisplayValue: row.DisplayValue, // Assuming row is the DisplayValue
                    selected: false,
                    Value: row.Value,
                };

                const matchingDynamicValue = this.stateValues.Values[key].find(
                    (value) => value === row.Value
                );

                if (matchingDynamicValue !== undefined) {
                    dynamicValue.selected = true;
                }

                if (this.stateValues.Values[key].length === 1 && this.stateValues.Values[key][0] === '# ALL #') {
                    dynamicValue.selected = true;
                }

                this.selectedReportFilterableFields[key]?.DynamicValues.push(dynamicValue);
            }
        }
        else {
            delete this.selectedReportFilterableFields[key];
        }
    }

    /**
     * Set the models for the filters from a loaded moduleManager state.
     * This function initializes and configures the models used in the dynamic filters component.
     * */
    public async setModels() {
        if (this.isInitializing === true) 
            return;
        this.isInitializing = true;

        this.stateValues = cloneDeep(this.moduleManagerCopy);
        this.selectedStaticFilterableFields = {};
        this.selectedDynamicFilterableFields = {};

        for (const key in this.selectedReportFilterableFields) {
            let filterField = this.selectedReportFilterableFields[key];

            //here I check to see if it has a state value
            if (this.stateValues.Values[key]) {
                if (filterField.ValueSource === FilterableFieldSource.Static) {
                    if (this.selectedStaticFilterableFields[key] === undefined)
                        this.selectedStaticFilterableFields[key] = [];

                    for (let staticValue of filterField.StaticValues) {
                        const matchingDynamicValue = this.stateValues.Values[key].find(
                            (value) => value === staticValue.Value
                        );

                        if (matchingDynamicValue !== undefined) {
                            staticValue.selected = true;
                            this.selectedStaticFilterableFields[key].push(staticValue);
                        }
                    }
                }
                else {
                    if (this.selectedDynamicFilterableFields[key] === undefined)
                        this.selectedDynamicFilterableFields[key] = [];

                    filterField.DynamicValues = [];
                    await this.compareAndPopulateDynamicValues(filterField.ValueSourceReportingSet, filterField.ValueDisplayField, key);
                    for (let dynamicValue of filterField.DynamicValues){
                        if (dynamicValue?.selected){
                            this.selectedDynamicFilterableFields[key].push(dynamicValue);
                        }
                    }    
                }

                continue;
            }

            await this.loadInitialFilterSelection(key);
        }
        this.isInitializing = false;
    }

    /**
     * Handle the selection of multiple filter values.
     * This function updates the selected dynamic and static filterable fields based on the user's selection.
     * @param event The event object containing the selected values
     * @param key The key identifying the filter field
     */
    public multiFilterSelected(event: any, key: string) {
        if (this.selectedDynamicFilterableFields[key]) {
            this.selectedDynamicFilterableFields[key] = event.selected;
        }
        else if (this.selectedStaticFilterableFields[key]) {
            this.selectedStaticFilterableFields[key] = event.selected;
        }

        //here I'm setting the stateValues that are going to be saved to ModuleManager
        this.stateValues.Values[key] = [];
        event.selected.forEach(element => {
            this.stateValues.Values[key].push(element.Value);
        });
    }

    /**
     * Handle the selection of a single filter value.
     * This function updates the state values based on the user's selection of a single filter value.
     * @param event The event object containing the selected value
     * @param key The key identifying the filter field
     */
    public singleFilterSelected(event: any, key: string) {
        this.stateValues.Values[key] = [event.selected.Value];
    }

    /**
     * Get the localization string for a given key.
     * This function retrieves the localized text for the specified key from the localization factory.
     * @param key Key for localized string to retrieve
     * @param defaultValue Value to return if error occurs
     * @returns The localized string or the default value if an error occurs
     */
    public getLocalizationString(key: string, defaultValue: string) {
        key = this.replaceSpacesWithUnderscores(key);
        return this.cvLocalizationFactory.getCachedText("Report_Widget", key, defaultValue);
    }

    /**
     * Check if the selected dynamic filterable fields are empty or they meet the minimum required and make a min selection if required.
     * This function ensures that the minimum required selections are met by selecting additional options if necessary.
     * @param key The key identifying the filter field
     */
    public CheckEmpty(key: string) {
        let minimumRequired = this.selectedReportFilterableFields[key].MinRequiredSelections;
        if((this.selectedDynamicFilterableFields[key]?.length < minimumRequired || this.selectedStaticFilterableFields[key]?.length < minimumRequired) && minimumRequired > 0){
            this.selectMinFilterOptions(key);
        } 
        // Empty filter
        else if ((this.selectedDynamicFilterableFields[key]?.length === 0 || this.selectedStaticFilterableFields[key]?.length === 0) && minimumRequired === -1) {
            this.selectMaxFilterOptions(key);
        }
    }

    /**
     * Select the maximum allowed options for a given key.
     * This functions selects options for the specified key up to the max allowed selections.
     * @param key The key identifying the filter field
     */
    private selectMaxFilterOptions(key: string) {
        let filterField = this.selectedReportFilterableFields[key];

        if (filterField.ValueSource === FilterableFieldSource.Static) {
            const maxSelections = filterField.MaxAllowedSelections !== -1 ? filterField.MaxAllowedSelections : filterField.StaticValues?.length;
            if (this.selectedStaticFilterableFields[key]?.length !== maxSelections) {
                this.selectedStaticFilterableFields[key] = [];
                this.stateValues.Values[key] = [];
            }
            else return;

            for (let i = 0; i < maxSelections; i++) {
                filterField.StaticValues[i].selected = true;
                this.selectedStaticFilterableFields[key].push(filterField.StaticValues[i]);
                this.stateValues.Values[key].push(filterField.StaticValues[i].Value);
            }
            
        } else if (filterField.ValueSource === FilterableFieldSource.ReportingSetSourced) {
            const maxSelections = filterField.MaxAllowedSelections !== -1 ? filterField.MaxAllowedSelections : filterField.DynamicValues?.length;
            if (this.selectedDynamicFilterableFields[key]?.length !== maxSelections) {
                this.selectedDynamicFilterableFields[key] = [];
                this.stateValues.Values[key] = [];
            }
            else return;
            
            for (let i = 0; i < maxSelections; i++) {
                filterField.DynamicValues[i].selected = true;
                this.selectedDynamicFilterableFields[key].push(filterField.DynamicValues[i]);
                this.stateValues.Values[key].push(filterField.DynamicValues[i].Value);
            }
        }
    }

    /**
     * Select the minimum required options for a given key.
     * This functions selects options for the specified key up to the minimum required selections.
     * @param key The key identifying the filter field
     */
    private selectMinFilterOptions(key: string) {
        let minimumRequired = this.selectedReportFilterableFields[key].MinRequiredSelections;

        if (this.selectedReportFilterableFields[key].ValueSource === 0) {
            // Add your logic here for when ValueSource is 0
            let selectedFilterNumber = this.selectedStaticFilterableFields[key].length; // Initial number of filters selected

            // Get not selected filterable fields
            for (let i = 0; i < this.selectedReportFilterableFields[key].StaticValues.length; i++) {
                if (selectedFilterNumber >= minimumRequired)
                    break;

                // Skip already selected filter
                if (!this.selectedReportFilterableFields[key].StaticValues[i].selected) {
                    this.selectedReportFilterableFields[key].StaticValues[i].selected = true;
                    this.selectedStaticFilterableFields[key].push(this.selectedReportFilterableFields[key].StaticValues[i]); 
                    selectedFilterNumber++;
                }
            }
        } else {
            let selectedFilterNumber = this.selectedDynamicFilterableFields[key].length; // Initial number of filters selected

            // Get not selected filterable fields
            for (let i = 0; i < this.selectedReportFilterableFields[key].DynamicValues.length; i++) {
                if (selectedFilterNumber >= minimumRequired)
                    break;

                // Skip already selected filter
                if (!this.selectedReportFilterableFields[key].DynamicValues[i].selected) {
                    this.selectedReportFilterableFields[key].DynamicValues[i].selected = true;
                    this.selectedDynamicFilterableFields[key].push(this.selectedReportFilterableFields[key].DynamicValues[i]);
                    selectedFilterNumber++; 
                }
            }
        }
    }

    replaceSpacesWithUnderscores(name: string): string {
        return name.replace(/\s+/g, '_');
    }
}
